import { classNames } from "@/utils/classNames"
import { RefetchItemsContext } from "@/utils/context/refetchItemsStackedList"
import { ComponentType } from "react"

interface StackedListProps<
  T,
  P extends Record<string, unknown> = Record<string, never>,
> {
  items: T[]
  ItemComponent: ComponentType<{ item: T } & Partial<P>>
  refetchItems?: () => void
  dataTestId?: string
  extraItemProps?: P
  // Stable identity per item. Without it the list keys by array index, so React keeps a
  // mounted item's own state (open/collapsed, an enabled query) at that slot when the list
  // reorders, and the state ends up on whichever item lands there next. Pass this whenever
  // the items can be reordered or replaced and ItemComponent holds state of its own.
  getItemKey?: (item: T, index: number) => string | number
}

export const StackedList = <
  T,
  P extends Record<string, unknown> = Record<string, never>,
>({
  items,
  ItemComponent,
  refetchItems,
  dataTestId,
  extraItemProps,
  getItemKey,
}: StackedListProps<T, P>) => {
  const extra = (extraItemProps ?? {}) as Partial<P>

  return (
    <RefetchItemsContext.Provider value={refetchItems || (() => {})}>
      <ul
        className={classNames("flex flex-col", "list-none")}
        data-testid={dataTestId}
      >
        {items.map((item, index) => (
          <li
            key={getItemKey ? getItemKey(item, index) : index}
            className={classNames(
              index !== items.length - 1 && "border-b border-grey4",
            )}
          >
            <ItemComponent item={item} {...extra} />
          </li>
        ))}
      </ul>
    </RefetchItemsContext.Provider>
  )
}
